/**
 * MACD Analyzer Types
 *
 * Type definitions for the MACD (Moving Average Convergence Divergence) Analyzer.
 * These types define the API contract between TypeScript and Rust.
 * The Rust implementation mirrors these types with serde annotations.
 *
 * @see docs/MACD_ANALYZER_PROPOSAL.md for detailed design documentation
 */
// ============================================================================
// Enums
// ============================================================================
/**
 * MACD Zone Classification
 * 7 zones providing granular classification of MACD state
 */
export var MACDZone;
(function (MACDZone) {
    /** MACD >> 0, MACD > Signal, histogram expanding */
    MACDZone["strongBullish"] = "strongBullish";
    /** MACD > 0, MACD > Signal */
    MACDZone["bullish"] = "bullish";
    /** MACD > Signal but MACD <= 0 OR histogram contracting */
    MACDZone["weakBullish"] = "weakBullish";
    /** |MACD - Signal| < threshold (potential crossover) */
    MACDZone["neutral"] = "neutral";
    /** MACD < Signal but MACD >= 0 OR histogram contracting */
    MACDZone["weakBearish"] = "weakBearish";
    /** MACD < 0, MACD < Signal */
    MACDZone["bearish"] = "bearish";
    /** MACD << 0, MACD < Signal, histogram expanding */
    MACDZone["strongBearish"] = "strongBearish";
})(MACDZone || (MACDZone = {}));
/**
 * Histogram Zone Classification
 * 7 zones for histogram momentum state
 */
export var MACDHistogramZone;
(function (MACDHistogramZone) {
    /** histogram > 0, expanding */
    MACDHistogramZone["strongPositive"] = "strongPositive";
    /** histogram > 0, stable or contracting */
    MACDHistogramZone["positive"] = "positive";
    /** 0 < histogram < threshold */
    MACDHistogramZone["weakPositive"] = "weakPositive";
    /** |histogram| < threshold */
    MACDHistogramZone["zero"] = "zero";
    /** -threshold < histogram < 0 */
    MACDHistogramZone["weakNegative"] = "weakNegative";
    /** histogram < 0, stable or contracting */
    MACDHistogramZone["negative"] = "negative";
    /** histogram < 0, expanding */
    MACDHistogramZone["strongNegative"] = "strongNegative";
})(MACDHistogramZone || (MACDHistogramZone = {}));
/**
 * MACD Trend Classification
 * Based on MACD position and direction relative to signal and zero
 */
export var MACDTrend;
(function (MACDTrend) {
    /** MACD > 0, rising, above signal */
    MACDTrend["strongBullish"] = "strongBullish";
    /** MACD > signal, rising or stable */
    MACDTrend["bullish"] = "bullish";
    /** MACD > signal but falling or near zero */
    MACDTrend["weakBullish"] = "weakBullish";
    /** No clear trend */
    MACDTrend["neutral"] = "neutral";
    /** MACD < signal but rising or near zero */
    MACDTrend["weakBearish"] = "weakBearish";
    /** MACD < signal, falling or stable */
    MACDTrend["bearish"] = "bearish";
    /** MACD < 0, falling, below signal */
    MACDTrend["strongBearish"] = "strongBearish";
})(MACDTrend || (MACDTrend = {}));
/**
 * MACD Momentum Classification
 * Based on histogram behavior
 */
export var MACDMomentum;
(function (MACDMomentum) {
    /** Histogram positive and expanding */
    MACDMomentum["acceleratingUp"] = "acceleratingUp";
    /** Histogram positive but contracting */
    MACDMomentum["deceleratingUp"] = "deceleratingUp";
    /** Histogram near zero or unchanged */
    MACDMomentum["stable"] = "stable";
    /** Histogram negative but contracting */
    MACDMomentum["deceleratingDown"] = "deceleratingDown";
    /** Histogram negative and expanding */
    MACDMomentum["acceleratingDown"] = "acceleratingDown";
})(MACDMomentum || (MACDMomentum = {}));
/**
 * MACD Histogram Direction
 * Simple directional classification of histogram movement
 */
export var MACDHistogramDirection;
(function (MACDHistogramDirection) {
    /** Histogram increasing (more positive or less negative) */
    MACDHistogramDirection["rising"] = "rising";
    /** Histogram decreasing (more negative or less positive) */
    MACDHistogramDirection["falling"] = "falling";
    /** Histogram unchanged or within noise threshold */
    MACDHistogramDirection["stable"] = "stable";
})(MACDHistogramDirection || (MACDHistogramDirection = {}));
/**
 * MACD Event Types
 * All possible events generated by the MACD analyzer
 */
export var MACDEventType;
(function (MACDEventType) {
    // === Signal Line Crossovers (P1 - Primary Signals) ===
    /** MACD crossed above Signal line */
    MACDEventType["bullishSignalCross"] = "bullishSignalCross";
    /** MACD crossed below Signal line */
    MACDEventType["bearishSignalCross"] = "bearishSignalCross";
    // === Zero Line Crossovers (P1 - Trend Confirmation) ===
    /** MACD crossed above zero */
    MACDEventType["bullishZeroCross"] = "bullishZeroCross";
    /** MACD crossed below zero */
    MACDEventType["bearishZeroCross"] = "bearishZeroCross";
    // === Zone Transitions (P1) ===
    MACDEventType["enteredStrongBullish"] = "enteredStrongBullish";
    MACDEventType["enteredBullish"] = "enteredBullish";
    MACDEventType["enteredWeakBullish"] = "enteredWeakBullish";
    MACDEventType["enteredNeutral"] = "enteredNeutral";
    MACDEventType["enteredWeakBearish"] = "enteredWeakBearish";
    MACDEventType["enteredBearish"] = "enteredBearish";
    MACDEventType["enteredStrongBearish"] = "enteredStrongBearish";
    // === Histogram Events (P2) ===
    /** Histogram crossed from negative to positive */
    MACDEventType["histogramTurnedPositive"] = "histogramTurnedPositive";
    /** Histogram crossed from positive to negative */
    MACDEventType["histogramTurnedNegative"] = "histogramTurnedNegative";
    /** Histogram reached a local peak (positive side) */
    MACDEventType["histogramPeaked"] = "histogramPeaked";
    /** Histogram reached a local trough (negative side) */
    MACDEventType["histogramTroughed"] = "histogramTroughed";
    // === Divergences (P3) ===
    /** Price lower low, MACD higher low */
    MACDEventType["bullishDivergenceDetected"] = "bullishDivergenceDetected";
    /** Price higher high, MACD lower high */
    MACDEventType["bearishDivergenceDetected"] = "bearishDivergenceDetected";
    /** Price higher low, MACD lower low (continuation) */
    MACDEventType["hiddenBullishDivergence"] = "hiddenBullishDivergence";
    /** Price lower high, MACD higher high (continuation) */
    MACDEventType["hiddenBearishDivergence"] = "hiddenBearishDivergence";
    /** Divergence was confirmed by price action */
    MACDEventType["divergenceConfirmed"] = "divergenceConfirmed";
    // === Crossover Prediction (P4) ===
    /** Bullish signal crossover is imminent */
    MACDEventType["bullishCrossoverImminent"] = "bullishCrossoverImminent";
    /** Bearish signal crossover is imminent */
    MACDEventType["bearishCrossoverImminent"] = "bearishCrossoverImminent";
    // === Confluence (P1 - multi-config) ===
    /** All MACD configs are bullish (above signal) */
    MACDEventType["allConfigsBullish"] = "allConfigsBullish";
    /** All MACD configs are bearish (below signal) */
    MACDEventType["allConfigsBearish"] = "allConfigsBearish";
})(MACDEventType || (MACDEventType = {}));
/**
 * MACD Divergence Types
 */
export var MACDDivergenceType;
(function (MACDDivergenceType) {
    /** Price lower low, MACD higher low (reversal up) */
    MACDDivergenceType["bullish"] = "bullish";
    /** Price higher high, MACD lower high (reversal down) */
    MACDDivergenceType["bearish"] = "bearish";
    /** Price higher low, MACD lower low (continuation up) */
    MACDDivergenceType["hiddenBullish"] = "hiddenBullish";
    /** Price lower high, MACD higher high (continuation down) */
    MACDDivergenceType["hiddenBearish"] = "hiddenBearish";
})(MACDDivergenceType || (MACDDivergenceType = {}));
/**
 * MACD Divergence Strength Classification
 */
export var MACDDivergenceStrength;
(function (MACDDivergenceStrength) {
    /** Large divergence in both MACD and price */
    MACDDivergenceStrength["strong"] = "strong";
    /** Moderate divergence */
    MACDDivergenceStrength["moderate"] = "moderate";
    /** Small divergence, potentially noise */
    MACDDivergenceStrength["weak"] = "weak";
})(MACDDivergenceStrength || (MACDDivergenceStrength = {}));
//# sourceMappingURL=MACDAnalyzerTypes.js.map